'use client';

import { type FC, useTransition } from 'react';
import { useRouter } from '@bprogress/next/app';
import { X } from 'lucide-react';

import { URLBuilder } from '@/utils/URLBuilder';
import { APIBARA_FILTER_PARAMS } from '@/utils/auctions/apibaraFilters';
import { getAppliedFilterChips, type TApibaraExtras } from '@/utils/auctions/appliedFilterChips';
import { FILTER_PARAMS, type TLotFilters } from '@/utils/auctions/lotFilters';
import { toAuctionSlug } from '@/utils/auctions/auctionSlugs';

/**
 * The applied-filter chips, above the results (spec 013, FR-023).
 *
 * Moved out of the form so the row stays visible whichever home the form is
 * in — inside a closed drawer it would be invisible exactly when a visitor
 * needs to know why the list is short. Chips describe what is CURRENTLY
 * APPLIED: they read the URL-derived filters, never the form's state.
 */

type TAuctionAppliedFilters = {
  filters: TLotFilters;
  extras: TApibaraExtras;
  /** The raw address parameters, so removal keeps every filter it does not name. */
  searchParams: Record<string, string | string[] | undefined>;
};

export const AuctionAppliedFilters: FC<TAuctionAppliedFilters> = ({
  filters,
  extras,
  searchParams,
}) => {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const chips = getAppliedFilterChips({ filters, extras });

  if (chips.length === 0) return null;

  const removeChip = (removes: string[]) => {
    const params = new URLSearchParams();

    // Every current parameter except the removed ones, the cursor (a removal
    // always returns to the first page) and make/model, which live in the path.
    for (const [key, value] of Object.entries(searchParams)) {
      if (
        value === undefined ||
        removes.includes(key) ||
        key === APIBARA_FILTER_PARAMS.cursor ||
        key === FILTER_PARAMS.make ||
        key === FILTER_PARAMS.model ||
        key === FILTER_PARAMS.page
      ) {
        continue;
      }

      if (Array.isArray(value)) value.forEach(entry => params.append(key, entry));
      else params.set(key, value);
    }

    // Dropping the make drops the model with it — /aukcje/x5 would be meaningless.
    const keepsMake = !removes.includes(FILTER_PARAMS.make) && filters.make;
    const keepsModel = keepsMake && !removes.includes(FILTER_PARAMS.model) && filters.model;

    startTransition(() => {
      router.push(
        URLBuilder.paths.getAuctionLots(
          1,
          params,
          keepsMake ? toAuctionSlug(filters.make as string) : null,
          keepsModel ? toAuctionSlug(filters.model as string) : null
        )
      );
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs uppercase tracking-[0.14em] text-foreground/40">Aktywne filtry</span>
      {chips.map(chip => (
        <button
          key={chip.key}
          type="button"
          onClick={() => removeChip(chip.removes)}
          disabled={isPending}
          className="group inline-flex items-center gap-1.5 rounded-full border border-neutral-700 bg-backgroundLight/60 py-1 pl-3 pr-2 text-xs text-foreground/80 transition-all duration-200 hover:-translate-y-0.5 hover:border-action hover:text-action disabled:opacity-60"
        >
          {chip.label}
          <X size={13} aria-hidden className="opacity-60 group-hover:opacity-100" />
          <span className="sr-only">Usuń filtr</span>
        </button>
      ))}
    </div>
  );
};
